import { GraphModel } from '../graph/graph.model';
import { z } from 'zod/v3';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import { AskResponse } from '../actions/framework/types';
import { useState } from 'react';
import { Loading } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ModalPageView } from '@cotera/client/app/components/app';
import { DocumentModel } from './document.model';
import { Routes } from '../routes';
import { DG } from '@cotera/sdk/core';

type Params = {};

export class SaveDocumentAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Save Document';
  description = 'Save current document as an expression';
  inputSchema = z.object({});
  shortcut = ['ctrl', 's'];
  group = 'Document';
  icon = 'save';

  constructor(
    private readonly navigator: {
      pushAndReplace: (props: { route: string }) => void;
    },
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      close: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return (
      context.isInScope(GraphModel) &&
      context.hasTargetedModelOfType(DocumentModel)
    );
  }

  async execute(
    _: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const graph = context.getInScopeModelOfType(GraphModel);
    const document = context.targetedModelOfType(DocumentModel);

    Assert.assert(graph !== null, 'Graph not found');
    Assert.assert(document !== null, 'Document not found');

    const saveToast = toast.loading('Saving document...', {
      id: 'save-document',
    });

    const result = document.compile({ setOperations: true });

    if (result.isErr()) {
      saveToast.error(JSON.stringify(result.error));
      return err([
        { t: result.error.t, message: JSON.stringify(result.error.e) },
      ]);
    }

    if (document.newOperations.snapshot().length === 0) {
      saveToast.done();
      return ok({});
    }

    const stagingResult = await graph.attemptToStageOperations(
      document.newOperations.snapshot()
    );

    if (stagingResult.isErr()) {
      const error = {
        t: stagingResult.error.err.t,
        message: JSON.stringify(stagingResult.error.err),
      };

      saveToast.error(error.message);
      return err([error]);
    }

    document.markAsStaged();

    saveToast.success(`Document ${document.name.snapshot()} saved`);

    if (document.opts.isNew) {
      this.navigator.pushAndReplace({
        route: Routes.document.forId(document.name.snapshot()).toString(),
      });
    }

    return ok({});
  }

  shouldAsk(context: ApplicableContext): boolean {
    return (
      context
        .targetedModelOfType(DocumentModel)
        ?.editorState.snapshot()
        .title() === null
    );
  }

  ask(_: {}, context: ExecuteContext): AskResponse<Params> {
    const model = context.targetedModelOfType(DocumentModel);

    Assert.assert(model !== null);

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.close();
                }}
                model={model}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

export const AskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  model: DocumentModel;
}> = ({ onRespond, model }) => {
  const [name, setName] = useState(model.name.snapshot());
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView title={`Save document as`} className="px-4 py-4">
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="document-name" className="text-sm">
            Document Name
          </Label>
          <Input
            id="document-name"
            placeholder="name"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="w-full flex justify-end">
          <Button
            disabled={loading}
            type="submit"
            variant="default"
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              const state = new DG.DocumentEditor.EditorState(
                model.formState.snapshot().documentState
              )
                .setTitle(name)
                .getState();
              model.formState.set({
                ...model.formState.snapshot(),
                documentState: state,
              });
              await onRespond({});
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
